import { ProfileBanner } from '@/app/components/ProfileBanner';
import { PATH } from '@/app/constants/path';
import BackgroundBannerImage from '@/components/BackgroundBannerImage';
import { useAuth0User } from '@/modules/Context/useAuth0User';
import LandingHeaderV2 from '@/modules/LandingHeaderV2';
import { Profile } from '@/types/profile';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useLayoutEffect } from 'react';

import Container from '@material-ui/core/Container';

import Footer from 'modules/Footer';

import styles from './style.module.scss';

const CookieConsentPrompt = dynamic(() => import('@/app/components/CookieConsentPrompt'));

export enum ContainerStyle {
  BLUE_BANNER = 'blueBanner',
  LITTLE_BLUE_BANNER = 'littleBlueBanner',
  SMALL_BLUE_BANNER = 'bgBannerSmallBanner',
  MEDIUM_BLUE_BANNER = 'mediumBlueBanner',
  BACKDROP_BLUE_BANNER = 'backdropBlueBanner',
  NO_BANNER = 'noBanner',
  TINY_BLUE_BANNER = 'tinyBlueBanner',
  PROFILE_BANNER_FF_DOT144 = 'profileBannerFF_DOT144',
  MEDWORLD_PROFILE = 'medworldProfileBanner',
  DARK_BLUE_BANNER = 'darkBlueBanner',
}

type SIZE = 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false | undefined;

interface UserLoggedinContainerProps {
  style?: ContainerStyle;
  profile?: Profile;
  size?: SIZE;
  className?: string;
  noFooterMargin?: boolean;
  children: React.ReactChild | React.ReactFragment | React.ReactPortal | boolean;
}

export const UserLoggedinContainer = ({
  style = ContainerStyle.BLUE_BANNER,
  size = 'lg',
  className,
  children,
  noFooterMargin,
}: UserLoggedinContainerProps) => {
  const router = useRouter();
  const { user, checkSession, isLoading } = useAuth0User();

  const getClassName = () => {
    let styleName: string;
    switch (style) {
      case ContainerStyle.BLUE_BANNER:
        styleName = styles.container;
        break;
      case ContainerStyle.LITTLE_BLUE_BANNER:
        styleName = styles.containerLittleBanner;
        break;
      case ContainerStyle.MEDIUM_BLUE_BANNER:
        styleName = styles.containerMediumBanner;
        break;
      case ContainerStyle.BACKDROP_BLUE_BANNER:
        styleName = styles.containerBackdropBanner;
        break;
      case ContainerStyle.NO_BANNER:
        styleName = styles.noBanner;
        break;
      case ContainerStyle.TINY_BLUE_BANNER:
        styleName = styles.containerTinyBlueBanner;
        break;
      case ContainerStyle.PROFILE_BANNER_FF_DOT144:
        styleName = styles.profileBannerContainerDOT144;
        break;
      case ContainerStyle.MEDWORLD_PROFILE:
        styleName = styles.medworldProfileBannerContainer;
        break;
      case ContainerStyle.SMALL_BLUE_BANNER:
        styleName = styles.containerSmallBlueBanner;
        break;
      case ContainerStyle.DARK_BLUE_BANNER:
        styleName = styles.containerDarkBlueBanner;
        break;
    }

    return styleName;
  };

  const getBackgroundImage = () => {
    if (style === ContainerStyle.NO_BANNER) {
      return;
    }
    if (style === ContainerStyle.PROFILE_BANNER_FF_DOT144) {
      return <div className={styles.profileBannerDOT144}></div>;
    }
    if (style === ContainerStyle.MEDWORLD_PROFILE) {
      return <div className={styles.medworldProfileBanner}></div>;
    }
    if (style === ContainerStyle.DARK_BLUE_BANNER) {
      return null;
    }
    let styleName: string;
    switch (style) {
      case ContainerStyle.BLUE_BANNER:
        styleName = styles.bgImageBlueBanner;
        break;
      case ContainerStyle.BACKDROP_BLUE_BANNER:
        styleName = styles.bgImageBackdropBanner;
        break;
      case ContainerStyle.LITTLE_BLUE_BANNER:
        styleName = styles.bgImageLittleBanner;
        break;
      case ContainerStyle.MEDIUM_BLUE_BANNER:
        styleName = styles.bgImageMediumBanner;
        break;
      case ContainerStyle.TINY_BLUE_BANNER:
        styleName = styles.bgImageTinyBanner;
        break;
      case ContainerStyle.SMALL_BLUE_BANNER:
        styleName = styles.bgBannerSmallBanner;
        break;
    }

    return (
      <div className={styleName}>
        <BackgroundBannerImage className={styles.bgImage} />
      </div>
    );
  };

  const onVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && !router.pathname.includes('/job')) {
      await checkSession();
    }
  };
  // In SSR useLayoutEffect is having an error at server side check
  // : https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
  if (typeof window !== 'undefined') {
    useLayoutEffect(() => {
      document.addEventListener('visibilitychange', onVisibilityChange);
      return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);
  }

  if (!isLoading && !user && router?.pathname && !router.pathname.includes('/job')) {
    router.push(PATH.LOGOUT);
  }

  return (
    <div className={styles.outsideContainer}>
      {getBackgroundImage()}
      <LandingHeaderV2 />
      <div className={getClassName()}>
        <Container maxWidth={size} className={className}>
          {children}
        </Container>
      </div>
      <Footer className={noFooterMargin ? styles.noFooterMargin : ''} />
      <CookieConsentPrompt />
      <ProfileBanner />
    </div>
  );
};
